import { useState } from 'react';
import type { InvitadoApuntado } from '../../api/ops/portalRrpp';
import { ErrorApi } from '../../api/problema';
import { claveIdempotencia } from '../../api/comun';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { Desplegable } from '../../componentes/Desplegable';
import { Icono } from '../../componentes/Icono';
import { PanelLateral } from '../../componentes/PanelLateral';
import { avisoBreveDeError } from '../../logica/estados';
import type { PropsPantalla } from '../tipos';
import { erroresAlta, sinProtocolo, textoApuntado } from './logica';
import type { DatosPortal } from './PortalRrpp';

/**
 * «Apuntar a un invitado» (10.12): evento, lista abierta, nombre y su Instagram o su email (uno de los dos; nunca teléfono,
 * G49). Sin Instagram conectado solo email (G517, G556): lo dicen los enlaces del RRPP (`unavailable_reasons`) y, si aun
 * así se intenta, la API responde `409 instagram_not_connected` (G788). La misma `Idempotency-Key` al repetir.
 */
export function Apuntar({ props, datos, evento: inicial, onCerrar, onApuntado, onCopiar }: { props: PropsPantalla; datos: DatosPortal; evento: string | null; onCerrar: () => void; onApuntado: (id: string) => void; onCopiar: (t: string) => void }) {
    const { negocio, movil, membresia } = props;
    const conListas = datos.eventos.filter((e) => (datos.listas[e.id] ?? []).some((l) => l.status === 'open'));
    const [evento, setEvento] = useState(inicial ?? conListas[0]?.id ?? '');
    const abiertas = (datos.listas[evento] ?? []).filter((l) => l.status === 'open');
    const [lista, setLista] = useState(abiertas[0]?.id ?? '');
    const [nombre, setNombre] = useState('');
    const [instagram, setInstagram] = useState('');
    const [email, setEmail] = useState('');
    const [acompanantes, setAcompanantes] = useState(0);
    const [errores, setErrores] = useState<ReturnType<typeof erroresAlta>>({});
    const [error, setError] = useState<string | null>(null);
    const [sinInstagram, setSinInstagram] = useState(() => datos.enlaces.some((e) => (e.unavailable_reasons ?? []).includes('instagram_not_connected')));
    const [ocupado, setOcupado] = useState(false);
    const [hecho, setHecho] = useState<InvitadoApuntado | null>(null);
    const [clave, setClave] = useState(claveIdempotencia);

    const elegida = abiertas.find((l) => l.id === lista);
    const max = elegida?.max_companions ?? 0;
    const cupo = elegida?.my_quota;

    const apuntar = () => {
        const e = erroresAlta({ nombre, instagram, email, lista }, !sinInstagram);
        setErrores(e);
        setError(null);
        if (Object.keys(e).length > 0) {
            return;
        }
        setOcupado(true);
        const ig = sinInstagram ? '' : instagram.trim().replace(/^@/, '');
        datos.ops
            .addGuest(
                negocio,
                {
                    guest_list_id: lista,
                    name: nombre.trim(),
                    ...(ig === '' ? {} : { instagram_username: ig }),
                    ...(email.trim() === '' ? {} : { email: email.trim() }),
                    preferred_channel: ig === '' ? 'email' : 'instagram',
                    companions: acompanantes,
                    ...(membresia === undefined ? {} : { member_id: membresia.member_id }),
                },
                clave,
            )
            .then((r) => { setHecho(r); })
            .catch((x: unknown) => {
                if (x instanceof ErrorApi && x.codigo === 'instagram_not_connected') {
                    setSinInstagram(true);
                    setError('El negocio no tiene Instagram conectado: apúntalo con su email.');
                } else if (x instanceof ErrorApi && x.estado === 409) {
                    setError(avisoBreveDeError(x) ?? 'No queda sitio en la lista o ya está cerrada.');
                } else {
                    setError(avisoBreveDeError(x) ?? 'No se ha podido apuntar. Prueba de nuevo.');
                }
                setClave(claveIdempotencia());
            })
            .finally(() => { setOcupado(false); });
    };

    if (hecho !== null) {
        const url = hecho.personal_link?.instagram_url ?? null;
        return (
            <PanelLateral abierto onCerrar={onCerrar} movil={movil} titulo="Apuntado" icono="user-plus" accion="Ver el invitado" accionIcono="chevron-right" onAccion={() => { onApuntado(hecho.guest.id); }}>
                <div className="rv-ca-cuerpo">
                    <div className="rv-ca-hecho"><span className="rv-ca-hecho__baldosa"><Icono nombre="check" /></span><span>{textoApuntado(hecho)}</span></div>
                    {url !== null && (
                        <div className="rv-ca-caja">
                            <strong>Su enlace personal</strong>
                            <code className="rv-rp-codigo">{sinProtocolo(url)}</code>
                            <Boton tipo="secundario" icono="copy" onClick={() => { onCopiar(url); }}>Copiar enlace</Boton>
                        </div>
                    )}
                </div>
            </PanelLateral>
        );
    }

    return (
        <PanelLateral abierto onCerrar={onCerrar} movil={movil} titulo="Apuntar a un invitado" subtitulo={sinInstagram ? 'Con su email' : 'Con su Instagram o su email'} icono="user-plus" accion="Apuntar" accionIcono="user-plus" ocupado={ocupado} ocupadoTexto="Apuntando…" inactiva={conListas.length === 0} onAccion={apuntar}>
            {conListas.length === 0 ? (
                <p className="rv-ca-nota"><Icono nombre="circle-alert" /><span>No tienes ninguna lista abierta ahora mismo.</span></p>
            ) : (
                <div className="rv-ca-cuerpo">
                    <Desplegable
                        etiqueta="Evento"
                        valor={evento}
                        opciones={conListas.map((e) => ({ valor: e.id, texto: e.name ?? '' }))}
                        onChange={(v) => {
                            setEvento(v);
                            setLista((datos.listas[v] ?? []).find((l) => l.status === 'open')?.id ?? '');
                            setAcompanantes(0);
                        }}
                    />
                    <Desplegable
                        etiqueta="Lista"
                        valor={lista}
                        opciones={abiertas.map((l) => ({ valor: l.id, texto: l.name ?? '' }))}
                        onChange={(v) => {
                            setLista(v);
                            setAcompanantes(0);
                        }}
                        error={errores.lista ?? null}
                        ayuda={cupo == null || cupo.quota == null ? null : `Te quedan ${String(Math.max(cupo.quota - (cupo.used ?? 0), 0))} de ${String(cupo.quota)}`}
                    />
                    <Campo etiqueta="Nombre y apellidos" valor={nombre} onChange={setNombre} error={errores.nombre ?? null} autocompletar="off" />
                    <strong className="rv-rp-subtitulo">{sinInstagram ? 'Su email' : 'Su Instagram o su email · uno de los dos'}</strong>
                    {!sinInstagram && <Campo etiqueta="Usuario de Instagram" valor={instagram} onChange={setInstagram} prefijo="@" ayuda="Le mandas tú el enlace por Instagram." error={errores.contacto ?? null} autocompletar="off" />}
                    <Campo etiqueta="Email" tipo="email" valor={email} onChange={setEmail} ayuda="Le mandamos el pase en el acto." error={errores.email ?? (sinInstagram ? (errores.contacto ?? null) : null)} autocompletar="off" />
                    {sinInstagram && <p className="rv-ca-nota rv-ca-nota--gris"><Icono nombre="instagram" /><span>Instagram sin conectar: de momento solo puedes apuntar por email.</span></p>}
                    {max > 0 && (
                        <div className="rv-ca-dato">
                            <span>{`Acompañantes · hasta ${String(max)}, cuentan para tu cupo`}</span>
                            <span className="rv-rp-contador">
                                <button type="button" aria-label="Uno menos" disabled={acompanantes <= 0} onClick={() => { setAcompanantes(acompanantes - 1); }}>−</button>
                                <strong aria-live="polite">{acompanantes}</strong>
                                <button type="button" aria-label="Uno más" disabled={acompanantes >= max} onClick={() => { setAcompanantes(acompanantes + 1); }}>+</button>
                            </span>
                        </div>
                    )}
                    {error !== null && <div role="alert" className="rv-ca-caja rv-ca-caja--error"><span>{error}</span></div>}
                </div>
            )}
        </PanelLateral>
    );
}
